import { useState } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import HelpTooltip from '@/components/help/HelpTooltip';

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d.key] = d.key === 'saturday' || d.key === 'sunday'
    ? { closed: true, open: '09:00', close: '18:00' }
    : { closed: false, open: '09:00', close: '18:00' };
  return acc;
}, {});

export default function BusinessHoursEditor({ initialHours, onSaved }) {
  const raw = initialHours || {};
  const interval = raw.deferred_release_interval_minutes ?? 5;
  const initial = {};
  DAYS.forEach(d => {
    initial[d.key] = raw[d.key] || DEFAULT_HOURS[d.key];
  });

  const [hours, setHours] = useState(initial);
  const [deferredMin, setDeferredMin] = useState(interval);
  // Por omisión encendido: es como funciona hoy. Apagarlo por defecto le
  // habría quitado la voz nocturna a quien ya la tiene, sin pedírselo.
  const [iaVozFuera, setIaVozFuera] = useState(raw.ia_voz_fuera_de_horario !== false);
  const [saving, setSaving] = useState(false);

  const setDay = (key, field, val) => {
    setHours(h => ({ ...h, [key]: { ...h[key], [field]: val } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...hours, deferred_release_interval_minutes: deferredMin };
      await api.patch('/tenant/business/business-hours',
                      { business_hours: payload, ia_voz_fuera_de_horario: iaVozFuera });
      toast.success('Horario guardado');
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="business-hours-editor">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-[#F72585]" />
        <h4 className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">
          Horario de atención
          <HelpTooltip text="La IA de voz y WhatsApp solo puede agendar citas dentro de estos horarios (hora del negocio). También usamos el horario para diferir asignación de leads fuera de turno." />
        </h4>
      </div>
      <p className="text-xs text-[#64748B]">
        Fuera de este horario: la IA puede seguir conversando, pero no debe confirmar citas (ej. no a las 6:00 si abres a las 9:00).
        Los leads se pueden asignar cuando abra el negocio. Para bloquear un tramo puntual (vacaciones) usa Agenda → Calendario → Bloquear espacio.
      </p>

      <div className="space-y-2">
        {DAYS.map(d => (
          <div key={d.key} className="flex items-center gap-3 py-1.5" data-testid={`hours-row-${d.key}`}>
            <span className="w-24 text-sm text-[#0F172A] font-medium">{d.label}</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={!hours[d.key]?.closed}
                onChange={e => setDay(d.key, 'closed', !e.target.checked)}
                className="rounded border-[#E2E8F0]"
                data-testid={`hours-toggle-${d.key}`} />
              <span className="text-xs text-[#64748B]">{hours[d.key]?.closed ? 'Cerrado' : 'Abierto'}</span>
            </label>
            {!hours[d.key]?.closed && (
              <div className="flex items-center gap-1.5 ml-auto">
                <input type="time" value={hours[d.key]?.open || '09:00'}
                  onChange={e => setDay(d.key, 'open', e.target.value)}
                  className="text-xs border border-[#E2E8F0] rounded px-2 py-1"
                  data-testid={`hours-open-${d.key}`} />
                <span className="text-xs text-[#94A3B8]">a</span>
                <input type="time" value={hours[d.key]?.close || '18:00'}
                  onChange={e => setDay(d.key, 'close', e.target.value)}
                  className="text-xs border border-[#E2E8F0] rounded px-2 py-1"
                  data-testid={`hours-close-${d.key}`} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-[#E2E8F0] pt-3">
        <label className="text-xs text-[#64748B] font-medium block mb-1">Intervalo liberación leads fuera de horario (minutos)</label>
        <input type="number" min={1} max={120} value={deferredMin}
          onChange={e => setDeferredMin(parseInt(e.target.value) || 5)}
          className="w-24 text-sm border border-[#E2E8F0] rounded px-2 py-1.5"
          data-testid="deferred-interval-input" />
      </div>

      {/* Qué pasa FUERA del horario. Va aquí porque quien acaba de definir su
          horario es quien tiene en la cabeza qué quiere cuando esté cerrado. */}
      <div className="border-t border-[#E2E8F0] pt-3" data-testid="fuera-de-horario">
        <label className="text-xs text-[#64748B] font-medium block mb-2">
          Cuando estás cerrado
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={iaVozFuera} className="mt-0.5"
                 onChange={(e) => setIaVozFuera(e.target.checked)}
                 data-testid="ia-voz-fuera-de-horario" />
          <span className="text-sm text-[#0F172A]">
            Que mi asistente de voz siga contestando llamadas
            <span className="block text-xs text-[#64748B]">
              Si lo apagas, quien llame de madrugada oirá un mensaje pidiéndole
              que vuelva a llamar en horario. Si lo dejas encendido, la IA
              atiende — es la ventaja de que no sea una persona.
            </span>
          </span>
        </label>
        {/* Esto NO es opcional y por eso no es una casilla: no se le suena el
            teléfono a un vendedor a las 3 de la mañana. */}
        <p className="text-xs text-[#64748B] mt-2 border-l-2 border-[#E2E8F0] pl-2">
          Pase lo que pase, fuera de tu horario <b>no se le hace sonar el
          teléfono a ningún vendedor</b>: la llamada enlazada por Twilio no se
          hace y el aviso le llega por WhatsApp para que lo lea al abrir.
        </p>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#0F172A] text-white w-full" data-testid="save-hours-btn">
        {saving ? 'Guardando...' : 'Guardar horario'}
      </Button>
    </div>
  );
}
